import { useSuspenseInfiniteQuery } from '@tanstack/react-query'

import { getRequest } from 'src/lib/api-client'
import { queries } from 'src/lib/query-keys'
import { useMyUserData } from 'src/shared/features/users/api/accounts/use-my-user'
import type { Profession } from 'src/shared/features/users/schemas/profession'
import type { ReferralShare } from 'src/shared/schemas/referrals/referral-share'

const getReferralSharesByCurrentUser = (params: {
  skip?: number
  limit?: number
  direction: 'sent' | 'received'
  referralId?: number
  profession?: Profession
  included_statuses?: string[]
  searchQuery?: string
  is_closed?: boolean
}) => {
  const queryParams = new URLSearchParams()

  if (params.skip !== undefined) queryParams.append('skip', params.skip.toString())
  if (params.limit !== undefined) queryParams.append('limit', params.limit.toString())
  if (params.direction !== undefined) queryParams.append('direction', params.direction)
  if (params.included_statuses) {
    params.included_statuses.forEach((status) => queryParams.append('included_statuses', status))
  }
  if (params.searchQuery) queryParams.append('query', params.searchQuery)
  if (params.is_closed !== undefined) queryParams.append('is_closed', params.is_closed.toString())
  if (params.referralId) queryParams.append('referral_id', params.referralId.toString())
  if (params.profession) queryParams.append('profession', params.profession)
  return getRequest<ReferralShare[]>(`/referral-shares/me?${queryParams.toString()}`, {})
}

export const useMyReferralShares = (
  direction: 'received' | 'sent',
  statuses: string[],
  searchQuery?: string,
  isClosed?: boolean,
  referralId?: number,
  profession?: Profession,
) => {
  const { data: currentUser } = useMyUserData()

  return useSuspenseInfiniteQuery({
    queryKey: queries.referralShares.list({
      direction,
      statuses,
      searchQuery,
      isClosed,
      referralId,
      profession,
      userId: currentUser?.id,
    }).queryKey,
    queryFn: ({ pageParam = 0 }) =>
      getReferralSharesByCurrentUser({
        skip: pageParam,
        limit: 6,
        included_statuses: statuses,
        searchQuery: searchQuery,
        direction: direction,
        is_closed: isClosed,
        referralId: referralId,
        profession: profession,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages) => {
      if (lastPage && lastPage.length === 6) {
        return pages.length * 6
      }
      return undefined
    },
    retry: false,
    staleTime: 1 * 60 * 1000,
  })
}
